import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./DecisionStatsItem.module.css";
import { nanoid } from "nanoid";

import ViewPdfButton from "ManagementTeam/Shared/ViewPdfButton";
import ThumbsUpButton from "ManagementTeam/Shared/ThumbsUpButton";
import LockIcon from "ManagementTeam/Shared/LockIcon";
import ThumbsDownButton from "ManagementTeam/Shared/ThumbsDownButton";
import PendingVoteButton from "ManagementTeam/Shared/PendingVoteButton";
import CircleCheckbox from "ManagementTeam/Shared/CircleCheckbox";
import Markdown from "ManagementTeam/Shared/Markdown";
import Toggler from "ManagementTeam/Shared/Toggler";
import Button from "shared/Button";
import Select from "ManagementTeam/Shared/Select";

import VotingResultsList from "../VotingResultsList";
import VoteCount from "../VoteCount";
import VotingRadioButtonList from "../VotingRadioButtonList";
import ResultsBlock from "../ResultsBlock";

import ApprovedIcon from "./Approved_Gray.svg";
import { ChevronRight } from "react-ikonate";
import { ChevronDown } from "react-ikonate";
import { CompleteButton } from "ManagementTeam/Shared/CompleteButton/CompleteButtonButton";
import { useDecisions } from "../useDecisions";
import Auth from "shared/@auth";

/**
 * A component that displays out a Decision Stat Item.
 */

export function DecisionStatsItem({
  assignee,
  assignedType,
  title,
  isDraft,
  document,
  description,
  isCompleted,
  votingStatus,
  pdfName,
  onClickPdf,
  onEdit,
  onDelete,
  onMove,
  onPublish,
  displayResults,
  voting,
  totalVotes,
  yesVotes,
  noVotes,
  diamonds,
  isBlockA,
  isBlockB,
  readOnly,
  onToggle,
  onChangeVotingStatus,
  decision,
  agendaId = "",
  handleProxyVote,
  isHideInfo,
}) {
  const { hasPermission } = Auth.useContainer();
  const isOwner = hasPermission("management-team.owners");

  const id = nanoid();
  const { votingProxyPut } = useDecisions();
  const [toggled, setToggled] = useState(false);
  const [checked, setChecked] = useState(isCompleted);
  const [showResultsList, setShowResultsList] = useState();
  const [showToggler, setShowToggler] = useState(voting === "closed");
  const [showLock, setShowLock] = useState(voting === "closed");
  const [_voting, setVoting] = useState(voting);

  const choiceYes = useMemo(() => {
    return decision?.choices?.find((a) => a.description == "YES");
  }, [decision]);

  const choiceNo = useMemo(() => {
    return decision?.choices?.find((a) => a.description == "NO");
  }, [decision]);

  useEffect(() => {
    setShowResultsList(false);
  }, [decision.meetingId]);

  useEffect(() => {
    setVoting(voting);
    setShowToggler(voting === "closed");
    setToggled(displayResults);
  }, [voting, displayResults]);
  const handleChange = (value) => {
    // eslint-disable-next-line default-case
    switch (value) {
      case "Edit":
        onEdit();
        break;
      case "Delete":
        onDelete(title, description);
        break;
      case "Move/Duplicate":
        onMove(title, description);
        break;
    }
  };

  const boxColor = (value) => {
    if (value === "Diamond") {
      return styles.diamond;
    } else if (value === "WWG") {
      return styles.wwg;
    } else if (value === "Attorney") {
      return styles.attorney;
    }
  };

  const handleToggle = () => {
    setToggled(!toggled);
    onToggle(!toggled);
  };

  const handleVoting = (value) => {
    if (value === "closed") {
      setShowToggler(true);
      setShowLock(true);
    } else {
      setShowToggler(false);
      setShowLock(false);
    }
    onChangeVotingStatus(value?.toLowerCase());
  };

  return (
    <>
      <div className={styles.flex}>
        <div className={styles.container}>
          <div className={styles.decisionContainer}>
            <div className={styles.topicContainer}>
              <div
                className={classNames(
                  styles.boxTypeLabel,
                  boxColor(assignedType)
                )}
              >
                {checked ? (
                  <img
                    src={ApprovedIcon}
                    alt="Approved"
                    onClick={() => setChecked(!checked)}
                    className={styles.image}
                  />
                ) : null}
              </div>
              <div>
                <div className={styles.infoAssignee}>{assignee}</div>
                <div>{title}</div>
                <div className={styles.infoDesc}>
                  <Markdown content={description} />
                </div>
              </div>
            </div>
            <div className={styles.votingContainer}>
              <div className={styles.radioList}>
                <VotingRadioButtonList
                  readOnly={readOnly}
                  onClick={(value) => {
                    handleVoting(value);
                    setVoting(value);
                  }}
                  status={_voting}
                />
              </div>
              {showToggler ? (
                <div className={styles.toggler}>
                  <div className={styles.votingSubtitle}>Display Results</div>
                  <Toggler
                    onChange={handleToggle}
                    toggled={toggled}
                    color={"#39B54A"}
                    key={id}
                    readOnly={readOnly}
                    disabled={readOnly}
                  />
                </div>
              ) : null}
            </div>
            {!isHideInfo && (
              <div className={styles.resultsContainer}>
                <>
                  <div className={styles.overview}>
                    {votingStatus === "Passed" ? (
                      <div className={styles.statusInfo}>
                        <div className={styles.lockIcon}>
                          {showLock && <LockIcon />}
                        </div>
                        <ThumbsUpButton isLocked={true} />
                        <div className={styles.passedText}>Passed!</div>
                      </div>
                    ) : null}

                    {votingStatus === "Failed" ? (
                      <div className={styles.statusInfo}>
                        <div className={styles.lockIcon}>
                          {showLock && <LockIcon />}
                        </div>
                        <ThumbsDownButton isLocked={true} />
                        <div className={styles.failedText}>Failed</div>
                      </div>
                    ) : null}

                    {votingStatus === "Voting in Progress" ? (
                      <div className={styles.statusInfo}>
                        <div className={styles.lockIcon}>
                          {showLock && <LockIcon />}
                        </div>
                        <PendingVoteButton isLocked={true} />
                        <div className={styles.pendingText}>
                          Voting in Progress
                        </div>
                      </div>
                    ) : null}

                    {votingStatus === "Upcoming" ? (
                      <div className={styles.statusInfo}>
                        <div className={styles.lockIcon}>
                          {showLock && <LockIcon />}
                        </div>
                        <PendingVoteButton isGrayed={true} isLocked={true} />
                        <div className={styles.pendingText}>Upcoming</div>
                      </div>
                    ) : null}

                    {votingStatus === "Complete" ? (
                      <div className={styles.statusInfo}>
                        <div className={styles.lockIcon}>
                          {showLock && <LockIcon />}
                        </div>
                        <CompleteButton isLocked={true} />
                        <div className={styles.pendingText}>Complete</div>
                      </div>
                    ) : null}

                    <div>
                      {/* TODO: (Decision) Modify below logic to adapt with block B Yes/No */}
                      <VoteCount
                        // votesMade={
                        //   isBlockA && isBlockB
                        //     ? yesVotesA + yesVotesB + noVotesA + noVotesB
                        //     : isBlockA && !isNaN(yesVotes + noVotes)
                        //     ? yesVotes + noVotes
                        //     : 0
                        // }
                        votesMade={decision?.voteCount}
                        totalVotes={decision?.attendanceCount}
                      />
                    </div>
                  </div>
                  <div className={styles.blockBreakdown}>
                    {isBlockA && !isBlockB && (
                      <div>
                        <ResultsBlock
                          thumbsUp={
                            isBlockA ? yesVotes : yesVotes ? yesVotes : 0
                          }
                          thumbsDown={
                            isBlockA ? noVotes : noVotes ? noVotes : 0
                          }
                          block={"A"}
                          thumbsUpPercentage={choiceYes?.percentage}
                          thumbsDownPercentage={choiceNo?.percentage}
                        />
                      </div>
                    )}
                    {isBlockA && isBlockB && (
                      <div className={styles.rowFlex}>
                        <ResultsBlock
                          thumbsUp={
                            isBlockA ? yesVotes : yesVotes ? yesVotes : 0
                          }
                          thumbsDown={
                            isBlockA ? noVotes : noVotes ? noVotes : 0
                          }
                          block={"A"}
                          thumbsUpPercentage={choiceYes?.percentage}
                          thumbsDownPercentage={choiceNo?.percentage}
                        />
                        <ResultsBlock
                          thumbsUp={
                            isBlockA ? yesVotes : yesVotes ? yesVotes : 0
                          }
                          thumbsDown={
                            isBlockA ? noVotes : noVotes ? noVotes : 0
                          }
                          block={"B"}
                          thumbsUpPercentage={
                            choiceYes?.capitalSharesPercentage
                          }
                          thumbsDownPercentage={
                            choiceNo?.capitalSharesPercentage
                          }
                        />
                      </div>
                    )}
                  </div>
                </>
              </div>
            )}
            <div className={styles.documentsContainer}>
              {document && (
                <>
                  <ViewPdfButton onClick={onClickPdf} />
                  <div>{pdfName}</div>
                </>
              )}
            </div>
            <div className={styles.completeContainer}>
              <CircleCheckbox
                readOnly={readOnly}
                checked={checked}
                onChange={() => setChecked(!checked)}
              />
            </div>
          </div>
          {showResultsList ? (
            <>
              <div
                className={styles.showResultsBar}
                onClick={() => setShowResultsList(!showResultsList)}
              >
                <div className={styles.barTitle}>
                  COLLAPSE RESULTS <ChevronDown fontSize="23px" />
                </div>
              </div>
              <div className={styles.votingResultsList}>
                <VotingResultsList
                  readOnly={readOnly}
                  diamonds={diamonds}
                  onSubmit={(e) => handleProxyVote(e, agendaId)}
                  isHideInfo={isHideInfo}
                />
              </div>
            </>
          ) : isOwner ? (
            <div
              className={styles.showResultsBar}
              onClick={() => setShowResultsList(!showResultsList)}
            >
              <div className={styles.barTitle}>
                EXPAND RESULTS <ChevronRight fontSize="23px" />
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
        {!readOnly && (
          <div className={styles.sideContainer}>
            <div className={styles.selectContainer}>
              <Select
                options={["Edit", "Delete", "Move/Duplicate"]}
                onChange={handleChange}
                placeholder={"Options"}
                className={styles.select}
              />
            </div>
            <Button
              purpose="save"
              children="Publish"
              className={styles.publishButton}
              onClick={() => {
                onPublish();
              }}
              style={{
                visibility: !readOnly && isDraft ? "visible" : "hidden",
              }}
              disabled={readOnly || !isDraft}
            />
          </div>
        )}
      </div>
    </>
  );
}

DecisionStatsItem.propTypes = {
  // Assignee of the decision (Bold Text)
  assignee: PropTypes.string,

  // The type of assigned item, either "Diamond”, "WWG”, or "Attorney”.  Left Brown Box (#7F6A45) is
  // Diamond, Left Teal Box (#91B6BB) is WWG, and Left Gray Box (#ADB7C0) is Attorney.
  assignedType: PropTypes.string,

  // Title of the decision item (Regular Text)
  title: PropTypes.string,

  // Description of the details of the decision item (Italic Text)
  description: PropTypes.string,

  // Boolean that holds if the item is completed. A checkmark will show on the left and right if it is.
  // User can click on either check mark to switch between true/false
  isCompleted: PropTypes.bool,

  // The current voting status of the decision item (Passed, Failed, Pending, Upcoming, Complete or Tie vote)
  votingStatus: PropTypes.string,

  // A string that holds the name of the PDF file for the View PDF Button
  pdfName: PropTypes.string,

  // Function that’s used to view the PDF when clicking the View PDF Button
  onClickPdf: PropTypes.func,

  // Function called when edit is clicked
  onEdit: PropTypes.func,

  // Functin called when delete is clicked
  onDelete: PropTypes.func,

  // Function called when Move/Duplicate is clicked
  onMove: PropTypes.func,

  // Function called when published is clicked
  onPublish: PropTypes.func,

  // Decided if results will be displayed. Toggler in the voting section.
  displayResults: PropTypes.bool,

  // Sting that is off on or closed to be displayed in the voting section
  voting: PropTypes.string,

  // Number of total votes
  totalVotes: PropTypes.number,

  // Number of thumbs up
  yesVotes: PropTypes.number,

  // Number of thumbs down votes
  noVotes: PropTypes.number,

  // Array of diamonds to display in the results section
  diamonds: PropTypes.array,

  decision: PropTypes.any,
  // voteCount: PropTypes.number,
  // attendanceCount: PropTypes.number
};

DecisionStatsItem.defaultProps = {
  diamonds: [],
  description: "",
};
